import ClientApplication from '../../api/application/interfaces/ClientApplication'
import type { WisconsinSpouseDTO } from '../../api/application/postApplication'
import { relationOfCoapplicant } from '../../form/fields/relationOfCoapplicant'
import { convertIncomeSourceStringToIds } from './sharedDTOHelpers'
import {
    coapplicantFieldNames,
    applicantFieldNames,
    otherFieldNames,
    hiddenFormValues,
} from './sharedInstructions'
import {
    coapplicantWisconsinFieldNames,
    wisconsinFieldNames,
} from './wisconsinFieldNames'

type ExtraDataFields = Record<string, unknown>

function wisconsinSpouseDataToFormValues(
    spouse: WisconsinSpouseDTO | undefined,
    fieldNames: typeof wisconsinFieldNames,
): ExtraDataFields {
    if (!spouse) {
        return {}
    }

    return {
        [fieldNames.maritalStatus]: spouse.marital_status,
        [fieldNames.spouseFirstName]: spouse.spouse_first_name,
        [fieldNames.spouseLastName]: spouse.spouse_last_name,
        [fieldNames.spouseStreet]: spouse.spouse_address,
        [fieldNames.spouseApt]: spouse.spouse_apt,
        [fieldNames.spouseCity]: spouse.spouse_city,
        [fieldNames.spouseState]: spouse.spouse_state,
        [fieldNames.spouseZip]: spouse.spouse_zip,
    }
}

/**
 * Relationship normalization map generated from dropdown field options.
 * Ensures single source of truth - if dropdown options change, this map updates automatically.
 * Keys are normalized to uppercase with spaces replaced by underscores for case-insensitive matching.
 */
const RELATIONSHIP_MAP: Record<string, string> = Object.fromEntries(
    relationOfCoapplicant.config.options.map((opt) => [
        opt.value.toUpperCase().replace(/\s+/g, '_'),
        opt.value,
    ]),
)

/**
 * Normalizes coapplicant relationship values to match the expected format.
 * Handles case-insensitive matching and returns the proper casing.
 * @param relationship Raw relationship string from API (e.g., "SPOUSE", "Spouse", "spouse")
 * @returns Normalized relationship value (e.g., "Spouse") or original value if no match
 */
function normalizeRelationship(
    relationship: string | null | undefined,
): string | undefined {
    if (!relationship) {
        return undefined
    }

    const normalized = relationship.trim()
    // Replace spaces with underscores for consistent lookup
    const upper = normalized.toUpperCase().replace(/\s+/g, '_')

    return RELATIONSHIP_MAP[upper] || normalized
}

/**
 * Common applicant conversion logic shared across all application types
 * Converts primary applicant and coapplicant data from ClientApplication to form format
 * @param app The ClientApplication object
 * @param extraFields Optional callback to add application-type-specific fields
 * @returns Converted form data for the application
 */
export function convertAppValuesBase(
    app: ClientApplication,
    extraFields?: (data: ExtraDataFields) => ExtraDataFields,
): ExtraDataFields {
    const dob = app.dob.replaceAll('/', ' / ')

    const monthlyHousingPayment = app.monthly_housing_payment
        ? app.monthly_housing_payment.toString()
        : '0'

    const employerMonthlyGross = app.employer_monthly_gross
        ? app.employer_monthly_gross.toString()
        : '0'

    const employerGrossAmount = app.employer_gross_amount
        ? app.employer_gross_amount.toString()
        : employerMonthlyGross

    const employerGrossInterval = app.employer_gross_interval
        ? app.employer_gross_interval
        : 'monthly'

    const hasOtherIncome = !!(
        app.other_income_source ||
        app.other_monthly_gross_income ||
        app.other_income_gross_amount ||
        app.other_income_gross_interval
    )

    const otherIncomeSource = convertIncomeSourceStringToIds(
        app.other_income_source,
    )

    const otherMonthlyGrossIncome = app.other_monthly_gross_income?.toString()

    const otherIncomeGrossAmount = app.other_income_gross_amount
        ? app.other_income_gross_amount.toString()
        : otherMonthlyGrossIncome

    const otherIncomeGrossInterval =
        !app.other_income_gross_interval && hasOtherIncome
            ? 'monthly'
            : app.other_income_gross_interval

    const data: ExtraDataFields = {
        [hiddenFormValues.group]: app.group,
        [otherFieldNames.applicationType]:
            app.application_type === 'J' ? 'joint' : 'individual',
        [otherFieldNames.financeManager]: app.finance_manager,
        [applicantFieldNames.ssn]: app.social_security_number,
        [applicantFieldNames.firstName]: app.first_name,
        [applicantFieldNames.middleInitial]: app.middle_initial,
        [applicantFieldNames.lastName]: app.last_name,
        [applicantFieldNames.suffix]: app.suffix,
        [applicantFieldNames.dateOfBirth]: dob,
        [applicantFieldNames.email]: app.email_address,
        [applicantFieldNames.street]: app.primary_address,
        [applicantFieldNames.city]: app.primary_city,
        [applicantFieldNames.state]: app.primary_state,
        [applicantFieldNames.residenceStatus]: app.residential_status,
        [applicantFieldNames.residentialYears]:
            app.residential_years?.toString(),
        [applicantFieldNames.residentialMonths]:
            app.residential_months?.toString(),
        [applicantFieldNames.apartmentSuite]: app.primary_apt,
        [applicantFieldNames.zip]: app.primary_zip,
        [applicantFieldNames.homePhone]: app.home_phone,
        [applicantFieldNames.mobilePhone]: app.mobile_phone,
        [applicantFieldNames.workPhone]: app.work_phone,
        [applicantFieldNames.monthlyHousingPayment]: monthlyHousingPayment,
        [applicantFieldNames.employmentStatus]: app.employment_status,
        [applicantFieldNames.employerMonthlyGross]: employerMonthlyGross,
        [applicantFieldNames.employerGrossAmount]: employerGrossAmount,
        [applicantFieldNames.employerGrossInterval]: employerGrossInterval,
        [applicantFieldNames.hasOtherIncome]: hasOtherIncome,
        [applicantFieldNames.otherIncomeSource]: otherIncomeSource,
        [applicantFieldNames.otherMonthlyGrossIncome]: otherMonthlyGrossIncome,
        [applicantFieldNames.otherIncomeGrossAmount]: otherIncomeGrossAmount,
        [applicantFieldNames.otherIncomeGrossInterval]:
            otherIncomeGrossInterval,
        [applicantFieldNames.employerYears]: app.employer_years?.toString(),
        [applicantFieldNames.employerMonths]: app.employer_months?.toString(),
        [applicantFieldNames.employerName]: app.employer_name,
        [applicantFieldNames.jobTitle]: app.job_title,
        id: app.id,
        uuid: app.uuid,
        ...wisconsinSpouseDataToFormValues(
            app.wisconsin_spouse,
            wisconsinFieldNames,
        ),
    }

    // Add any extra fields specific to the application type
    const dataWithExtras = extraFields ? extraFields(data) : data

    if (app.application_type === 'J') {
        const coapplicant_dob = app.coapplicant_dob.replaceAll('/', ' / ')

        const coapplicant_monthlyHousingPayment =
            app.coapplicant_monthly_housing_payment
                ? app.coapplicant_monthly_housing_payment.toString()
                : '0'

        const coapplicant_employerMonthlyGross =
            app.coapplicant_employer_monthly_gross
                ? app.coapplicant_employer_monthly_gross.toString()
                : '0'

        const coapplicant_employerGrossAmount =
            app.coapplicant_employer_gross_amount
                ? app.coapplicant_employer_gross_amount.toString()
                : coapplicant_employerMonthlyGross

        const coapplicant_employerGrossInterval =
            app.coapplicant_employer_gross_interval
                ? app.coapplicant_employer_gross_interval
                : 'monthly'

        const coapplicant_hasOtherIncome = !!(
            app.coapplicant_other_income_source ||
            app.coapplicant_other_monthly_gross_income ||
            app.coapplicant_other_income_gross_amount ||
            app.coapplicant_other_income_gross_interval
        )

        const coapplicant_otherIncomeSource = convertIncomeSourceStringToIds(
            app.coapplicant_other_income_source,
        )

        const coapplicant_otherMonthlyGrossIncome =
            app.coapplicant_other_monthly_gross_income?.toString()

        const coapplicant_otherIncomeGrossAmount =
            app.coapplicant_other_income_gross_amount
                ? app.coapplicant_other_income_gross_amount.toString()
                : coapplicant_otherMonthlyGrossIncome

        const coapplicant_otherIncomeGrossInterval =
            !app.coapplicant_other_income_gross_interval &&
            coapplicant_hasOtherIncome
                ? 'monthly'
                : app.coapplicant_other_income_gross_interval

        return {
            ...dataWithExtras,
            [otherFieldNames.relationOfCoapplicant]: normalizeRelationship(
                app.coapplicant_relationship,
            ),
            [coapplicantFieldNames.ssn]: app.coapplicant_social_security_number,
            [coapplicantFieldNames.firstName]: app.coapplicant_first_name,
            [coapplicantFieldNames.middleInitial]:
                app.coapplicant_middle_initial,
            [coapplicantFieldNames.lastName]: app.coapplicant_last_name,
            [coapplicantFieldNames.suffix]: app.coapplicant_suffix,
            [coapplicantFieldNames.dateOfBirth]: coapplicant_dob,
            [coapplicantFieldNames.email]: app.coapplicant_email_address,
            [coapplicantFieldNames.street]: app.coapplicant_primary_address,
            [coapplicantFieldNames.city]: app.coapplicant_primary_city,
            [coapplicantFieldNames.state]: app.coapplicant_primary_state,
            [coapplicantFieldNames.residenceStatus]:
                app.coapplicant_residential_status,
            [coapplicantFieldNames.residentialYears]:
                app.coapplicant_residential_years?.toString(),
            [coapplicantFieldNames.residentialMonths]:
                app.coapplicant_residential_months?.toString(),
            [coapplicantFieldNames.apartmentSuite]: app.coapplicant_primary_apt,
            [coapplicantFieldNames.zip]: app.coapplicant_primary_zip,
            [coapplicantFieldNames.homePhone]: app.coapplicant_home_phone,
            [coapplicantFieldNames.mobilePhone]: app.coapplicant_mobile_phone,
            [coapplicantFieldNames.workPhone]: app.coapplicant_work_phone,
            [coapplicantFieldNames.monthlyHousingPayment]:
                coapplicant_monthlyHousingPayment,
            [coapplicantFieldNames.employmentStatus]:
                app.coapplicant_employment_status,
            [coapplicantFieldNames.employerMonthlyGross]:
                coapplicant_employerMonthlyGross,
            [coapplicantFieldNames.employerGrossAmount]:
                coapplicant_employerGrossAmount,
            [coapplicantFieldNames.employerGrossInterval]:
                coapplicant_employerGrossInterval,
            [coapplicantFieldNames.hasOtherIncome]: coapplicant_hasOtherIncome,
            [coapplicantFieldNames.otherIncomeSource]:
                coapplicant_otherIncomeSource,
            [coapplicantFieldNames.otherMonthlyGrossIncome]:
                coapplicant_otherMonthlyGrossIncome,
            [coapplicantFieldNames.otherIncomeGrossAmount]:
                coapplicant_otherIncomeGrossAmount,
            [coapplicantFieldNames.otherIncomeGrossInterval]:
                coapplicant_otherIncomeGrossInterval,
            [coapplicantFieldNames.employerYears]:
                app.coapplicant_employer_years?.toString(),
            [coapplicantFieldNames.employerMonths]:
                app.coapplicant_employer_months?.toString(),
            [coapplicantFieldNames.employerName]: app.coapplicant_employer_name,
            [coapplicantFieldNames.jobTitle]: app.coapplicant_job_title,
            ...wisconsinSpouseDataToFormValues(
                app.coapplicant_wisconsin_spouse,
                coapplicantWisconsinFieldNames,
            ),
        }
    }

    return dataWithExtras
}
